/**
 * Hook moderno para obtener datos de Your Habits usando Tanstack Query
 * Migrado de Apollo Client a fetch nativo para mejor rendimiento
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { yourHabitsApi } from '../services/contentfulService'
import type { YourHabitsBeneficioItem } from '../types/contentful.types'

interface ProcessedYourHabitsData {
	isHabitsData: YourHabitsBeneficioItem | null
	isStreams: YourHabitsBeneficioItem | null
	isParQCard: YourHabitsBeneficioItem | null
	lifestyleQuestionnaire: YourHabitsBeneficioItem | null
}

/**
 * Hook principal para obtener y procesar datos de Your Habits
 */
const useYourHabits = () => {
	const {
		data,
		isLoading: loading,
		error,
		refetch,
		isStale,
		isRefetching,
		dataUpdatedAt
	} = useQuery({
		queryKey: ['contentful', 'your-habits'],
		queryFn: yourHabitsApi.getYourHabits,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Log de errores
	if (error) {
		console.error('Error fetching your habits data:', error)
	}

	// Procesar y distribuir los datos
	const processedData = useMemo((): ProcessedYourHabitsData => {
		if (!data) {
			return {
				isHabitsData: null,
				isStreams: null,
				isParQCard: null,
				lifestyleQuestionnaire: null
			}
		}

		const currentData =
			data?.homeCollection?.items?.[0]?.vistas?.contenidoEntrenaTusHabitos?.beneficioCollection
				?.items

		if (!currentData) {
			return {
				isHabitsData: null,
				isStreams: null,
				isParQCard: null,
				lifestyleQuestionnaire: null
			}
		}

		// Distribuir datos según el idReto (misma lógica que el archivo original)
		const isHabitsData = currentData.find(item => item?.idReto === 'habitos') || null
		const isStreams = currentData.find(item => item?.idReto === 'streams') || null
		const isParQCard =
			currentData.find(item => item?.idReto === 'parq')?.beneficioCollection?.items?.[0] || null
		const lifestyleQuestionnaire =
			currentData.find(item => item?.idReto === 'lifestyleQuestionnaire') || null

		return {
			isHabitsData,
			isStreams,
			isParQCard,
			lifestyleQuestionnaire
		}
	}, [data])

	// Información del cache
	const cacheInfo = useMemo(() => {
		const cacheExpiry = dataUpdatedAt ? new Date(dataUpdatedAt + 5 * 24 * 60 * 60 * 1000) : null
		return {
			cacheExpiry,
			isStale,
			isRefetching,
			lastUpdated: dataUpdatedAt ? new Date(dataUpdatedAt) : null
		}
	}, [dataUpdatedAt, isStale, isRefetching])

	return {
		// Estados principales
		loading,
		error,
		...processedData,

		// Funciones de control
		refetch,

		// Información del cache
		cacheInfo,
		isStale,
		isRefetching,

		// Datos raw para debugging
		rawData: data
	}
}

export default useYourHabits
